import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
// config
import { volunteerHomePath } from 'src/config-global';
// context
import { useOrganizationContext } from 'src/context/organization';
import { useTenantConfig } from 'src/context/tenant';
// hooks
import { useIsAppOnlyUser } from 'src/hooks/use-app-only-user';
import { useEffectiveRole } from 'src/hooks/use-effective-role';
// routes
import { usePathname, useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
// utils
import { isAssignedTasksPath, isWebCallingEnabled } from 'src/utils/assigned-tasks/calling-access';

// ----------------------------------------------------------------------

// Everything a volunteer is allowed to reach: their assigned calling tasks (only for an org in
// WEB_CALLING_ENABLED_ORG_IDS — see callingBlocked below) and voter search.
// Route trees are all registered unconditionally, so this is what actually keeps them out of
// the manager surfaces.
//
// This was written as a backstop for a case that "shouldn't arise", because web only offered
// orgs the user managed. That is no longer true — volunteers hold orgs now (see the Assigned
// Tasks spec), so this guard is the primary mechanism and not a safety net.
const VOLUNTEER_PATH_PREFIXES = [paths.assignedTasks.root, paths.voters.root, paths.voters.legRoot];

// A national_viewer reads the performance reports (every level lives on the Activity page) and the
// National dashboard, nothing else. The API refuses the rest too; this keeps the screens honest.
const NATIONAL_VIEWER_PATH_PREFIXES = [paths.reports.activity, paths.national.root];

const isWithin = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * Keeps the rendered screen consistent with the user's role *in the selected organization*.
 *
 * Two jobs:
 *  1. Confine an effective volunteer to the volunteer surfaces, and a national_viewer to the
 *     reports and the National dashboard (see above).
 *  2. On an org switch that promotes them (volunteer -> manager), lift them out of the
 *     volunteer screen into the manager experience.
 *
 * Role comes from useEffectiveRole, so superadmin always resolves first (a superadmin who
 * happens to hold a volunteer membership row is still a superadmin) and owner/volunteer are
 * normalized onto manager/user.
 */
export default function EffectiveRoleGuard({ children }) {
  const router = useRouter();
  const pathname = usePathname();
  const effectiveRole = useEffectiveRole();
  const { isSelectionResolved, roleInSelectedOrganization, selectedOrganizationId } =
    useOrganizationContext();
  const { isLegislative } = useTenantConfig();
  // Someone who only volunteers while WEB_CALLING_ENABLED is off (see isAppOnlyUser): the
  // app-download page is the one place in the web portal they belong, whatever the URL.
  const isAppOnly = useIsAppOnlyUser();

  const prevRoleRef = useRef(null);

  // Same answer PATH_AFTER_LOGIN (src/config-global.js) gives: a volunteer bounced out of a
  // manager surface arrives where their own login would have taken them.
  const volunteerHome = volunteerHomePath(
    roleInSelectedOrganization,
    isLegislative,
    selectedOrganizationId
  );

  // Assigned Tasks — the list and the call session — is a volunteer surface only for an org in
  // WEB_CALLING_ENABLED_ORG_IDS. Elsewhere a volunteer is sent to voter search, whether they
  // typed the URL or switched into such an org while on it. (Managers are not moved: they meet
  // the "not enabled" card in layouts/dashboard/main.js.)
  const callingBlocked =
    isAssignedTasksPath(pathname) && !isWebCallingEnabled(selectedOrganizationId);

  const inVolunteerScope =
    !callingBlocked && VOLUNTEER_PATH_PREFIXES.some((prefix) => isWithin(pathname, prefix));

  // Until the selected organization settles, `user` may be only the global-role fallback — many
  // managers are volunteers globally. Acting on it would bounce a manager off their own screen
  // (to Assigned Tasks) on every load, so hold the screen blank instead and decide once the
  // real role is known.
  const awaitingRole = effectiveRole === 'user' && !isSelectionResolved && !inVolunteerScope;
  const outOfScope =
    !isAppOnly && effectiveRole === 'user' && isSelectionResolved && !inVolunteerScope;
  const viewerOutOfScope =
    effectiveRole === 'national_viewer' &&
    !NATIONAL_VIEWER_PATH_PREFIXES.some((prefix) => isWithin(pathname, prefix));

  useEffect(() => {
    if (isAppOnly) {
      router.replace(paths.volunteers.root);
    } else if (outOfScope) {
      router.replace(volunteerHome);
    } else if (viewerOutOfScope) {
      router.replace(paths.reports.activity);
    }
  }, [isAppOnly, outOfScope, viewerOutOfScope, router, volunteerHome]);

  useEffect(() => {
    // The fallback role is not a role to transition *from* either: recording it would read the
    // settling of a global-volunteer manager's real role as a promotion and yank them to the
    // dashboard root.
    if (!isSelectionResolved) return;

    const prev = prevRoleRef.current;

    if (effectiveRole) {
      prevRoleRef.current = effectiveRole;
    }

    // Only act on a real transition between two known roles — not on first paint, and not
    // while the org role is still resolving (effectiveRole falls back to the global role
    // until then, so an empty value means "unknown", never "volunteer").
    if (!prev || !effectiveRole || prev === effectiveRole) return;

    if (
      prev === 'user' &&
      (effectiveRole === 'manager' || effectiveRole === 'legislative_member')
    ) {
      router.replace(
        effectiveRole === 'legislative_member' ? paths.voters.legSearch : paths.dashboard.root
      );
    }
    // Demotion (manager -> volunteer) needs no special case: if the current screen isn't a
    // volunteer surface, `outOfScope` above already redirects.
  }, [effectiveRole, isSelectionResolved, router]);

  if (isAppOnly || outOfScope || viewerOutOfScope || awaitingRole) return null;

  return children;
}

EffectiveRoleGuard.propTypes = {
  children: PropTypes.node,
};
